import { useEffect, useState } from 'react';
import uploadTable from '@sdk/utils/api/uploadTable';
import gettext, { ngettext } from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useToast from '@sdk/common/toast/useToast';
import { LocationsImportDataItem } from '@sdk/locations/api/LocationsApi';

const defaultPickedColumnsMapDict: Record<string, boolean> = {
  name: false,
  address: false,
  externalId: false,
  phone: false,
  timezone: false,
};

const makePickedColumsDict = (columnMap: Record<number, string>): Record<string, boolean> => {
  return Object.values(columnMap).reduce(
    (dict, col) => {
      if (col) {
        dict[col] = true;
      }
      return dict;
    },
    { ...defaultPickedColumnsMapDict }
  );
};

const makeSelectedRowsIndices = (rowSelection: Record<number, boolean>): number[] => {
  return Object.entries(rowSelection).reduce((array, [key, value]) => {
    if (value) {
      array.push(key);
    }

    return array;
  }, []);
};

export const useImportLocations = (acceptedFiles, onLocationsImported, close) => {
  const [uploadError, setUploadError] = useState<string>();
  const [isUploading, setIsUploading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [parsedFileData, setParsedFileData] = useState<string[][]>();
  const [updateExistingLocations, setUpdateExistingLocations] = useState(false);
  const [defaultCountryCode, setDefaultCountryCode] = useState<string>();
  const [columnMap, setColumnMap] = useState<Record<number, string>>(null);
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>(null);
  const api = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();

  useEffect(() => {
    if (isUploading) {
      return;
    }

    setUploadError(null);

    const uploadFile = async () => {
      setIsUploading(true);
      try {
        const data = await uploadTable(acceptedFiles[0]);
        setParsedFileData(data);
      } catch (e) {
        if (e?.response?.status === 400) {
          setUploadError(gettext('The file type is not supported.'));
        } else {
          setUploadError(e?.data?.message || gettext('Something went wrong. Try again.'));
        }
      } finally {
        setIsUploading(false);
      }
    };

    if (acceptedFiles?.length) {
      uploadFile();
    }

    /**
     * We want to run this effect only when the acceptedFiles change
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles]);

  const pickedColumnsDict = columnMap
    ? makePickedColumsDict(columnMap)
    : defaultPickedColumnsMapDict;
  const selectedRowsIndices = rowSelection ? makeSelectedRowsIndices(rowSelection) : [];

  const submit = async () => {
    if (!pickedColumnsDict.name) {
      setErrorText(gettext('At least location name label is required'));
      throw 'Invalid data';
    }

    if (!selectedRowsIndices.length) {
      setErrorText(gettext('No rows selected'));
      throw 'Invalid data';
    }

    try {
      const data = parsedFileData
        .filter((d, i) => rowSelection[i])
        .map((locationRow) => {
          return locationRow.reduce((obj, field, index) => {
            if (columnMap[index]) {
              obj[columnMap[index]] = field;
            }

            return obj;
          }, {} as LocationsImportDataItem);
        });

      // defaultCountryCode looks like this: "+1:US"
      const countryCode = defaultCountryCode && defaultCountryCode.split(':')[0];

      const { data: result } = await api.locations.importLocations({
        data,
        countryCode,
        updateExisting: updateExistingLocations,
      });

      onLocationsImported(result.locations);

      showSuccessToast(
        ngettext(
          '%(locationsImported)s location imported successfully. %(locationsSkipped)s skipped.',
          '%(locationsImported)s locations imported successfully. %(locationsSkipped)s skipped.',
          result.locations.length,
          {
            locationsImported: result.locations.length,
            locationsSkipped: result.locationsSkipped,
          }
        )
      );
      close();
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      throw e;
    }
  };

  const clearErrorText = () => {
    setErrorText('');
  };

  return {
    uploadError,
    parsedFileData,
    setParsedFileData,
    updateExistingLocations,
    setUpdateExistingLocations,
    defaultCountryCode,
    setDefaultCountryCode,
    columnMap,
    setColumnMap: (value) => {
      setColumnMap(value);
      clearErrorText();
    },
    rowSelection,
    setRowSelection: (value) => {
      setRowSelection(value);
      clearErrorText();
    },
    pickedColumnsDict,
    selectedRowsIndices,
    submit,
    errorText,
    clearErrorText,
  };
};
